import React, { useState, useMemo } from 'react';
import { Box, Typography, 
  FormControl, MenuItem, InputLabel, Select,   
} from '@mui/material';
import { getSortableValue, formatXValue } from 'state/helpers';
import BarMultiLinesChart from './BarMultiLinesChart';

const SortBarMultiLinesChart = ({ 
  data = [], 
  title = "",
  bottomLegend, 
  leftLegend, 
  isLinesVisible = true, 
  isSortVisible = true, 
  isHorizontalBottomLegend, 
  digit = 2,
  topOffSet = 0,
  bottomOffSet = 0,
  bottomLegendOffSet = 0, 
  itemWidthOffSet = 0,
  barTranslateXOffSet = 0,
  barTranslateYOffSet = 0,
  lineTranslateXOffSet = 0,
  lineTranslateYOffSet = 0,
  itemsSpacingOffSet = 0,
}) => {
  const [sortType, setSortType] = useState('xAxis');  

  const sortChartDataByXAxis = (chartData, xAxisName) => {
    if (!Array.isArray(chartData) || chartData.length < 1) {
      return [];
    }

    const getXValue = (item) => item[xAxisName] || item['x'];

    // Ensure that chartData[i].data is an iterable array
    const isDataArray = (data) => Array.isArray(data);

    // Create a sorted index map based on the first dataset (chartData[0])
    const sortedOrder = isDataArray(chartData[0]?.data) 
      ? [...chartData[0].data]
          .sort((a, b) => {
            const valueA = getSortableValue(getXValue(a));
            const valueB = getSortableValue(getXValue(b));
    
            return typeof valueA === "string" && typeof valueB === "string"
              ? valueA.localeCompare(valueB)
              : valueA - valueB;
          })
          .map(getXValue)
      : [];

    // Sorting function using precomputed sortedOrder
    const sortByOrder = (data) => {
      if (!isDataArray(data)) return []; // Prevent errors if data is not an array
      return [...data]
        .sort((a, b) => sortedOrder.indexOf(getXValue(a)) - sortedOrder.indexOf(getXValue(b)))
        .map((item) => ({
          ...item,
          [xAxisName]: formatXValue(getXValue(item)),
        }));
    };

    // Dynamically sort all available datasets in chartData
    return chartData.map((dataset) => {
      if (isDataArray(dataset.data)) {
        return {
          ...dataset,
          data: sortByOrder(dataset.data),
        };
      } else {
        // If data is not iterable, return the dataset as is
        return dataset;
      }
    });
  };

  const sortChartDataByYAxis = (chartData) => {
    if (chartData.length === 0) return [];
  
    // Sort the first dataset's data by y value
    const sortedFirstDataset = [...chartData[0].data].sort((a, b) => a.y - b.y);
  
    // Extract sorted x values
    const sortedXValues = sortedFirstDataset.map(item => item.x);
  
    // Apply the sorted x order to all datasets
    const sortedChartData = chartData.map(dataset => ({
      ...dataset,
      data: sortedXValues.map(xValue => {
        const matchingItem = dataset.data.find(d => d.x === xValue);
        return { 
          x: xValue, 
          y: matchingItem ? matchingItem.y : 0 // Preserve y if available, otherwise default to 0
        };
      })
    }));
    
    return sortedChartData.map(dataset => ({
      ...dataset,
      data: dataset.data.map(item => ({
        ...item,
        x: formatXValue(item.x) // Format x values 
      }))
    }));
  }; 

  const sortedData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return null;
    }
    
    const sort = {
      yAxis: () => sortChartDataByYAxis(data, 'y'),
      xAxis: () => sortChartDataByXAxis(data, 'x'),
    };

    return sort[sortType]?.() || data;
  }, [sortType, data]);
  
  if (!sortedData || sortedData.length === 0 || !Array.isArray(sortedData)) {
    return (
      <Box p={1} sx={{ height: '100%', width: '100%' }}>        
        <Typography>Nothing to display</Typography>
      </Box>
    );
  }
  
  
  return (
    <Box sx={{ height: '100%', width: '100%' }}>       
      {isSortVisible && (
        <FormControl 
          size="small"
          sx={{ m: "0.5rem" }}
        >
          <InputLabel>Sort</InputLabel>
          <Select
            value={sortType}
            label="Sort"
            onChange={(e) => setSortType(e.target.value)}            
          >
            <MenuItem value="xAxis">by xAxis</MenuItem>
            <MenuItem value="yAxis">by yAxis</MenuItem>                                            
          </Select>
        </FormControl>
      )} 
      
      <BarMultiLinesChart
        data={sortedData}
        title={title}
        bottomLegend={bottomLegend || "X-Axis"}
        leftLegend={leftLegend || "Y-Axis"}
        isLinesVisible={isLinesVisible}
        isHorizontalBottomLegend={isHorizontalBottomLegend}
        digit={digit}
        topOffSet={topOffSet}
        bottomOffSet={bottomOffSet}
        bottomLegendOffSet={bottomLegendOffSet} 
        itemWidthOffSet={itemWidthOffSet}
        barTranslateXOffSet={barTranslateXOffSet}
        barTranslateYOffSet={barTranslateYOffSet}
        lineTranslateXOffSet={lineTranslateXOffSet}
        lineTranslateYOffSet={lineTranslateYOffSet}
        itemsSpacingOffSet={itemsSpacingOffSet}
      />
    </Box>
  );
};

export default SortBarMultiLinesChart;
